import { useEffect, useMemo, useRef } from "react";
import {
  CONTENT_HUB_SOURCES,
  useContentHubEvents,
  flattenContentHubPages,
  type ContentHubItem,
  type HeadlinesPayload,
} from "@/features/content-hub";
import type { Headline, HeadlineTimestamp } from "../models/Headline";
import { headlinePortfolioMatcher } from "../utils";
import { playNotificationSound } from "@/utils/sound-utils";
import {
  getNotificationPermission,
  showBrowserNotification,
} from "@/utils/browser-notification-utils";
import { getHighestPriorityMatchingPortfolio } from "@/utils/portfolioUtils";
import { useUserPreferences } from "@/pages/settings/hooks/useUserPreferences";
import { useNotificationSoundSettings } from "@/pages/settings/hooks/useNotificationSoundSettings";
import { useBrowserNotificationSettings } from "@/pages/settings/hooks/useBrowserNotificationSettings";
import {
  useHeadlinesPortfoliosWithSoundEnabled,
  useHeadlinesPortfoliosWithPopupEnabled,
} from "@/shared/api/newsquawk-headlines/useHeadlinesPortfoliosWithSoundEnabled";

const toHeadlineTimestamp = (
  iso: string | null | undefined
): HeadlineTimestamp => {
  const ms = iso ? Date.parse(iso) : NaN;
  return {
    unix: Number.isNaN(ms) ? 0 : Math.floor(ms / 1000),
    text: "",
    iso_8601: iso ?? "",
  };
};

/** Stable positive-int hash of a string (djb2). Used to derive a numeric
 *  Headline id when the source id isn't numeric (e.g. string uuids), so ids
 *  stay unique and never NaN — a NaN id breaks React keys and the list's
 *  first-item comparison (NaN !== NaN). */
function hashStringToInt(value: string): number {
  let hash = 5381;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return hash >>> 0;
}

/** Map a normalized content-hub item into the widget's Headline model. Fields
 *  the content-hub payload doesn't carry (source, analysis/reaction details,
 *  slug, …) are simply absent — the item component tolerates that. */
export function mapContentHubItemToHeadline(item: ContentHubItem): Headline {
  const payload = (item.raw ?? {}) as Partial<HeadlinesPayload>;
  const tagIds = (payload.tags ?? [])
    .map(Number)
    .filter((n) => !Number.isNaN(n));
  const numericId = Number(payload.id ?? item.sourceId);
  return {
    id: Number.isFinite(numericId)
      ? numericId
      : hashStringToInt(item.publishId),
    subject: item.title,
    content: item.body ?? "",
    source: "",
    published_at: item.occursAt,
    created_at: toHeadlineTimestamp(payload.created_at),
    updated_at: toHeadlineTimestamp(payload.created_at),
    tag_id_list: tagIds,
    // Names hydrated on demand by useTagManagerHydrate in the item details
    tags: tagIds.map((tag_id) => ({ tag_id, name: "", score: 0 })),
    tickers: item.tickers,
    publish_id: item.publishId,
    isNew: item.receivedAt !== undefined && !item.wasUpdated,
    wasUpdated: item.wasUpdated,
    received_at: item.receivedAt,
  };
}

const getPublishedMs = (headline: Headline): number =>
  typeof headline.published_at === "object"
    ? headline.published_at.unix * 1000
    : new Date(headline.published_at).getTime();

interface Options {
  /** Gate on live access — when false nothing is fetched or streamed. */
  enabled?: boolean;
}

/**
 * Content-hub-backed replacement for the legacy `useHeadlines()` base feed.
 *
 * Historical data comes from the content-hub /v1/search query and live events
 * from the shared /v1/stream connection (both via useContentHubEvents); this
 * hook adapts them to the Headline model the widget renders, and reproduces
 * the legacy notification behavior (headline/watchlist sounds, browser
 * pop-ups) for genuinely new live items — not for updates/corrections or
 * items backfilled by a reconnect refetch.
 *
 * Pagination beyond the initial search page is a content-hub follow-up, so
 * `hasMore` is always false.
 */
export function useContentHubHeadlinesFeed({ enabled = true }: Options = {}) {
  const query = useContentHubEvents(CONTENT_HUB_SOURCES.HEADLINES, {
    enabled,
  });

  // Cache mapped headlines per content-hub item so row object identity is
  // stable across stream patches — the item component mutates show_details
  // on the object, and unstable identity would reset it and re-render rows.
  const mappedCacheRef = useRef(new WeakMap<ContentHubItem, Headline>());
  const mapItem = (item: ContentHubItem): Headline => {
    let headline = mappedCacheRef.current.get(item);
    if (!headline) {
      headline = mapContentHubItemToHeadline(item);
      mappedCacheRef.current.set(item, headline);
    }
    return headline;
  };
  const mapItemRef = useRef(mapItem);
  mapItemRef.current = mapItem;

  // Flatten the loaded pages (deduped by publishId) into a single list.
  const items = useMemo(
    () => flattenContentHubPages(query.data),
    [query.data]
  );

  const headlines = useMemo(() => {
    // Defensive sort, newest first — stream patches prepend by arrival order,
    // which can differ from occurs_at order.
    return items
      .map((item) => mapItemRef.current(item))
      .sort((a, b) => getPublishedMs(b) - getPublishedMs(a));
  }, [items]);

  // ── Notifications for new live items (parity with legacy useHeadlines) ──
  const { notificationsSound, isLoading: isUserPrefLoading } =
    useUserPreferences();
  const portfoliosWithSoundEnabledRef = useHeadlinesPortfoliosWithSoundEnabled();
  const { settings: notificationSoundSettings } = useNotificationSoundSettings();
  const notificationSoundSettingsRef = useRef(notificationSoundSettings);
  notificationSoundSettingsRef.current = notificationSoundSettings;
  const portfoliosWithPopupEnabledRef = useHeadlinesPortfoliosWithPopupEnabled();
  const { settings: browserNotifySettings } = useBrowserNotificationSettings();
  const browserNotifySettingsRef = useRef(browserNotifySettings);
  browserNotifySettingsRef.current = browserNotifySettings;

  const processedIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  useEffect(() => {
    if (query.data === undefined) return;

    // First load: everything is historical — record without alerting.
    if (!initializedRef.current) {
      items.forEach((item) => processedIdsRef.current.add(item.publishId));
      initializedRef.current = true;
      return;
    }

    for (const item of items) {
      if (processedIdsRef.current.has(item.publishId)) continue;
      processedIdsRef.current.add(item.publishId);

      // Alert only for items pushed over the live stream; reconnect-backfill
      // items (no receivedAt) and corrections stay silent, like legacy.
      if (!item.receivedAt || item.wasUpdated) continue;
      if (!enabled || isUserPrefLoading) continue;

      const headline = mapItemRef.current(item);

      if (notificationsSound.headlines) {
        playNotificationSound(notificationSoundSettingsRef.current.headlines);
      }
      const soundPortfolios = portfoliosWithSoundEnabledRef.current ?? [];
      if (
        soundPortfolios.length > 0 &&
        headlinePortfolioMatcher(headline, soundPortfolios)
      ) {
        playNotificationSound(notificationSoundSettingsRef.current.portfolio);
      }

      // Browser pop-up (independent of sound): a watchlist match wins over
      // the generic "all headlines" pop-up.
      if (
        browserNotifySettingsRef.current.enabled &&
        getNotificationPermission() === "granted"
      ) {
        const popupPortfolios = portfoliosWithPopupEnabledRef.current ?? [];
        const matchedPopup =
          popupPortfolios.length > 0
            ? getHighestPriorityMatchingPortfolio(
                headline,
                // Copy: getHighestPriorityMatchingPortfolio sorts in place.
                [...popupPortfolios],
                headlinePortfolioMatcher
              )
            : undefined;
        if (matchedPopup) {
          showBrowserNotification({
            title: matchedPopup.name,
            body: headline.subject,
            tag: `wl-${matchedPopup.id}`,
          });
        } else if (browserNotifySettingsRef.current.headlines) {
          showBrowserNotification({
            title: "Newsquawk Headline",
            body: headline.subject,
            tag: "headlines",
          });
        }
      }
    }
  }, [
    items,
    query.data,
    enabled,
    isUserPrefLoading,
    notificationsSound.headlines,
    portfoliosWithSoundEnabledRef,
    portfoliosWithPopupEnabledRef,
  ]);

  return {
    headlines,
    isLoading: query.isPending,
    isError: query.isError,
    error: query.error,
    hasMore: query.hasNextPage,
    loadNextPage: query.fetchNextPage,
    loadingNextPage: query.isFetchingNextPage,
    refetch: query.refetch,
  };
}
